import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { z } from "zod";
import { CheckCircle2, ImagePlus, ArrowLeft, LogOut, ClipboardList, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

export const Route = createFileRoute("/_authenticated/report-conditions")({
  head: () => ({
    meta: [
      { title: "Report Today's Conditions — Coffman Cove Lodging" },
      { name: "robots", content: "noindex" },
      {
        name: "description",
        content:
          "Anglers: log today's spot, species sightings, water conditions, and notes so we can improve future spot recommendations.",
      },
    ],
  }),
  component: ReportConditionsPage,
});

const MAX_MB = 15;
const ACCEPTED = ["image/jpeg", "image/png", "image/webp", "image/heic"];

const schema = z.object({
  reported_at: z.string().min(1, "Pick a date"),
  spot_name: z.string().trim().max(120).optional().or(z.literal("")),
  species: z.string().trim().max(120).optional().or(z.literal("")),
  catch_count: z
    .string()
    .trim()
    .max(6)
    .optional()
    .or(z.literal(""))
    .refine((v) => !v || /^\d+$/.test(v), "Enter a whole number"),
  tide: z.string().trim().max(60).optional().or(z.literal("")),
  swell: z.string().trim().max(60).optional().or(z.literal("")),
  wind: z.string().trim().max(60).optional().or(z.literal("")),
  weather: z.string().trim().max(80).optional().or(z.literal("")),
  water_clarity: z.string().trim().max(60).optional().or(z.literal("")),
  notes: z.string().trim().max(2000).optional().or(z.literal("")),
});

type PastReport = {
  id: string;
  reported_at: string;
  spot_name: string | null;
  species: string | null;
  catch_count: number | null;
  notes: string | null;
  created_at: string;
};

function todayISO() {
  const d = new Date();
  const tz = d.getTimezoneOffset() * 60000;
  return new Date(d.getTime() - tz).toISOString().slice(0, 10);
}

function ReportConditionsPage() {
  const navigate = useNavigate();
  const [userId, setUserId] = useState<string | null>(null);
  const [userEmail, setUserEmail] = useState<string | null>(null);

  const [reportedAt, setReportedAt] = useState(todayISO());
  const [spotName, setSpotName] = useState("");
  const [species, setSpecies] = useState("");
  const [catchCount, setCatchCount] = useState("");
  const [tide, setTide] = useState("");
  const [swell, setSwell] = useState("");
  const [wind, setWind] = useState("");
  const [weather, setWeather] = useState("");
  const [waterClarity, setWaterClarity] = useState("");
  const [notes, setNotes] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const [past, setPast] = useState<PastReport[]>([]);
  const [loadingPast, setLoadingPast] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data } = await supabase.auth.getUser();
      if (cancelled) return;
      setUserId(data.user?.id ?? null);
      setUserEmail(data.user?.email ?? null);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const loadPast = useCallback(async () => {
    setLoadingPast(true);
    const { data, error: err } = await supabase
      .from("condition_reports")
      .select("id, reported_at, spot_name, species, catch_count, notes, created_at")
      .order("reported_at", { ascending: false })
      .order("created_at", { ascending: false })
      .limit(10);
    if (!err && data) setPast(data as PastReport[]);
    setLoadingPast(false);
  }, []);

  useEffect(() => {
    void loadPast();
  }, [loadPast, done]);

  function onPickFile(f: File | null) {
    setError(null);
    if (!f) {
      setFile(null);
      setPreview(null);
      return;
    }
    if (!ACCEPTED.includes(f.type)) {
      setError("Please upload a JPG, PNG, WEBP, or HEIC image.");
      return;
    }
    if (f.size > MAX_MB * 1024 * 1024) {
      setError(`Image must be under ${MAX_MB} MB.`);
      return;
    }
    setFile(f);
    setPreview(URL.createObjectURL(f));
  }

  function resetForm() {
    setReportedAt(todayISO());
    setSpotName("");
    setSpecies("");
    setCatchCount("");
    setTide("");
    setSwell("");
    setWind("");
    setWeather("");
    setWaterClarity("");
    setNotes("");
    setFile(null);
    setPreview(null);
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!userId) {
      setError("You must be signed in.");
      return;
    }
    const parsed = schema.safeParse({
      reported_at: reportedAt,
      spot_name: spotName,
      species,
      catch_count: catchCount,
      tide,
      swell,
      wind,
      weather,
      water_clarity: waterClarity,
      notes,
    });
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? "Please review the form.");
      return;
    }

    setSubmitting(true);
    try {
      let photo_path: string | null = null;
      if (file) {
        const ext = file.name.split(".").pop()?.toLowerCase() ?? "jpg";
        const path = `${userId}/${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${ext}`;
        const { error: uploadErr } = await supabase.storage
          .from("condition-reports")
          .upload(path, file, { contentType: file.type, upsert: false });
        if (uploadErr) throw uploadErr;
        photo_path = path;
      }

      const payload = {
        user_id: userId,
        reported_at: reportedAt,
        spot_name: spotName.trim() || null,
        species: species.trim() || null,
        catch_count: catchCount ? Number(catchCount) : null,
        tide: tide.trim() || null,
        swell: swell.trim() || null,
        wind: wind.trim() || null,
        weather: weather.trim() || null,
        water_clarity: waterClarity.trim() || null,
        notes: notes.trim() || null,
        photo_path,
        submitter_email: userEmail,
      };
      const { error: insertErr } = await supabase.from("condition_reports").insert(payload);
      if (insertErr) throw insertErr;

      resetForm();
      setDone(true);
      setTimeout(() => setDone(false), 4000);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  async function signOut() {
    await supabase.auth.signOut();
    navigate({ to: "/auth", replace: true });
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="mx-auto max-w-3xl px-4 sm:px-6 py-10">
        <div className="flex items-center justify-between mb-6">
          <Link
            to="/fishing"
            className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft size={16} /> Back to Fishing
          </Link>
          <button
            type="button"
            onClick={signOut}
            className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground"
          >
            <LogOut size={14} /> Sign out
          </button>
        </div>

        <header className="mb-8">
          <div className="inline-flex items-center gap-2 rounded-full bg-[var(--color-ember)]/15 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-[var(--color-ember)]">
            <ClipboardList size={14} /> Angler Log
          </div>
          <h1 className="mt-3 text-3xl sm:text-4xl font-semibold tracking-tight">
            Report today's conditions
          </h1>
          <p className="mt-2 text-muted-foreground">
            Share what you saw on the water — spot, species, tide, weather, and any notes.
            Reports are private to you and the lodge and help us dial in spot recommendations.
          </p>
          {userEmail && (
            <p className="mt-2 text-xs text-muted-foreground">Signed in as {userEmail}</p>
          )}
        </header>

        {done && (
          <div className="mb-6 flex items-start gap-3 rounded-md border border-green-600/30 bg-green-50 px-4 py-3 text-sm text-green-900">
            <CheckCircle2 size={18} className="mt-0.5" />
            <div>
              <div className="font-semibold">Report saved.</div>
              <div>Thanks for the intel — logged below.</div>
            </div>
          </div>
        )}

        <form onSubmit={onSubmit} className="space-y-5 rounded-lg border border-border bg-card p-5 sm:p-6">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Date" required>
              <input
                type="date"
                required
                value={reportedAt}
                onChange={(e) => setReportedAt(e.target.value)}
                max={todayISO()}
                className={inputClass}
              />
            </Field>
            <Field label="Spot / area">
              <input
                type="text"
                value={spotName}
                onChange={(e) => setSpotName(e.target.value)}
                placeholder="e.g. Point Baker, Kashevarof Passage"
                className={inputClass}
              />
            </Field>
            <Field label="Species">
              <input
                type="text"
                value={species}
                onChange={(e) => setSpecies(e.target.value)}
                placeholder="King, Coho, Halibut, Ling, Rockfish…"
                className={inputClass}
              />
            </Field>
            <Field label="Number caught / seen">
              <input
                type="number"
                inputMode="numeric"
                min={0}
                value={catchCount}
                onChange={(e) => setCatchCount(e.target.value)}
                placeholder="e.g. 4"
                className={inputClass}
              />
            </Field>
            <Field label="Tide">
              <input
                type="text"
                value={tide}
                onChange={(e) => setTide(e.target.value)}
                placeholder="Incoming, slack, big ebb…"
                className={inputClass}
              />
            </Field>
            <Field label="Swell">
              <input
                type="text"
                value={swell}
                onChange={(e) => setSwell(e.target.value)}
                placeholder="Flat, 2 ft, sloppy…"
                className={inputClass}
              />
            </Field>
            <Field label="Wind">
              <input
                type="text"
                value={wind}
                onChange={(e) => setWind(e.target.value)}
                placeholder="Calm, 10 kt SE…"
                className={inputClass}
              />
            </Field>
            <Field label="Weather">
              <input
                type="text"
                value={weather}
                onChange={(e) => setWeather(e.target.value)}
                placeholder="Overcast, rain, sunny…"
                className={inputClass}
              />
            </Field>
            <Field label="Water clarity">
              <input
                type="text"
                value={waterClarity}
                onChange={(e) => setWaterClarity(e.target.value)}
                placeholder="Clear, green, murky…"
                className={inputClass}
              />
            </Field>
          </div>

          <Field label="Notes / observations">
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={4}
              maxLength={2000}
              placeholder="Bait, depth, boats around, bird activity, anything worth remembering…"
              className={inputClass}
            />
          </Field>

          <Field label="Photo (optional)">
            <label className="flex cursor-pointer items-center justify-center gap-2 rounded-md border border-dashed border-border bg-background px-4 py-6 text-sm text-muted-foreground hover:bg-[var(--color-parchment)]/40">
              <ImagePlus size={18} />
              {file ? file.name : "Attach a photo (JPG, PNG, WEBP, HEIC)"}
              <input
                type="file"
                accept={ACCEPTED.join(",")}
                className="hidden"
                onChange={(e) => onPickFile(e.target.files?.[0] ?? null)}
              />
            </label>
            {preview && (
              <img
                src={preview}
                alt=""
                className="mt-3 h-40 w-40 rounded-md object-cover border border-border"
              />
            )}
          </Field>

          {error && (
            <div className="rounded-md border border-red-500/30 bg-red-50 px-3 py-2 text-sm text-red-800">
              {error}
            </div>
          )}

          <div className="flex items-center justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={resetForm}
              disabled={submitting}
              className="rounded-md border border-border px-4 py-2 text-sm font-medium hover:bg-[var(--color-parchment)]/50 disabled:opacity-60"
            >
              Reset
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="inline-flex items-center gap-2 rounded-md bg-[var(--color-ember-deep)] px-5 py-2.5 text-sm font-semibold uppercase tracking-wide text-white shadow-sm transition-colors hover:bg-[var(--color-ember-deep)]/90 disabled:opacity-60"
            >
              {submitting && <Loader2 size={16} className="animate-spin" />}
              {submitting ? "Saving…" : "Submit report"}
            </button>
          </div>
        </form>

        <section className="mt-10">
          <h2 className="text-lg font-semibold mb-3">Your recent reports</h2>
          {loadingPast ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : past.length === 0 ? (
            <p className="text-sm text-muted-foreground">No reports yet. Your first one will appear here.</p>
          ) : (
            <ul className="divide-y divide-border rounded-lg border border-border bg-card">
              {past.map((r) => (
                <li key={r.id} className="p-4">
                  <div className="flex flex-wrap items-baseline justify-between gap-2">
                    <div className="font-medium">
                      {r.spot_name || "Unspecified spot"}
                      {r.species ? <span className="text-muted-foreground"> — {r.species}</span> : null}
                      {typeof r.catch_count === "number" ? (
                        <span className="text-muted-foreground"> · {r.catch_count}</span>
                      ) : null}
                    </div>
                    <time className="text-xs text-muted-foreground">{r.reported_at}</time>
                  </div>
                  {r.notes && (
                    <p className="mt-1 text-sm text-muted-foreground line-clamp-2">{r.notes}</p>
                  )}
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
}

const inputClass =
  "w-full rounded-md border border-border bg-background px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-[var(--color-ember)] focus:border-transparent";

function Field({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        {label}
        {required && <span className="text-[var(--color-ember)]"> *</span>}
      </span>
      {children}
    </label>
  );
}
